import { logActivity } from "./activity-log";

/**
 * De qué anuncio llegó cada cuenta (#1731).
 *
 * Los anuncios de Meta (#1302) llegan a /authentication?register=true&utm_source=…&utm_content=aNN.
 * Al cargar la app se guarda el PRIMER toque del navegador (no se pisa con visitas posteriores) y,
 * apenas hay sesión (registro, ingreso con clave o Google, o una visita después), se manda UNA vez
 * el evento `registro_origen`. Va por el log de actividad y no por el alta: no toca las tres puertas
 * de registro ni exige migración, y reusa la barrera PHI del backend, que solo acepta estas claves
 * como slug corto (`finetuning/activity.py`, familia `registro_origen`).
 * Cubre R1, R2 — .claude/specs/1731-utm-registro/
 */
export const CLAVE_ORIGEN = "smartdx.origen_registro.v1";
const CLAVES_UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const SLUG = /^[a-z0-9][a-z0-9_:-]{0,63}$/;

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

// "Paid Social" → "paid_social", "médicos" → "medicos". Es un filtro de FORMA (slug corto): los
// valores los arma quien hace el link del anuncio, y un nombre propio en minúscula pasaría igual,
// como en el backend. Lo que sí se descarta es cualquier corrida de 7+ dígitos aunque venga separada
// (un RUT `12.345.678-9` no puede llegar como `12_345_678-9`, F1 del revisor de #1731), y lo que no
// quede como slug válido.
export function normalizarUtm(valor) {
  const v = String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s.+/]+/g, "_")
    .replace(/[^a-z0-9_:-]/g, "")
    .slice(0, 64);
  if (/\d{7,}/.test(v.replace(/[._:\- ]/g, ""))) return null;
  return SLUG.test(v) ? v : null;
}

/** R1: guarda el primer toque con utm_* de esta URL. Devuelve lo guardado o null. */
export function capturarOrigen(search = typeof window !== "undefined" ? window.location.search : "", storage = safeStorage()) {
  try {
    if (!storage || storage.getItem(CLAVE_ORIGEN)) return null; // primer toque: no se pisa
    // Si el navegador YA tiene una sesión abierta (un cliente actual que vio un anuncio de
    // retargeting), ese clic no es de un registro nuevo: no se atribuye a esa cuenta ni se le quita
    // el primer toque al lead que se registre después en este navegador (F3 del revisor de #1731).
    if (storage.getItem("authToken")) return null;
    const params = new URLSearchParams(search || "");
    const origen = {};
    CLAVES_UTM.forEach((k) => {
      const v = normalizarUtm(params.get(k));
      if (v) origen[k] = v;
    });
    if (!Object.keys(origen).length) return null;
    const dato = { ...origen, at: Date.now(), enviado: false };
    storage.setItem(CLAVE_ORIGEN, JSON.stringify(dato));
    return dato;
  } catch (_) {
    return null;
  }
}

/** R2: con sesión y un origen sin mandar, manda `registro_origen` una sola vez. */
export function enviarOrigenSiHaySesion({ storage = safeStorage(), log = logActivity } = {}) {
  try {
    if (!storage || !storage.getItem("authToken")) return false;
    const raw = storage.getItem(CLAVE_ORIGEN);
    if (!raw) return false;
    const dato = JSON.parse(raw);
    if (!dato || dato.enviado) return false;
    const meta = {};
    CLAVES_UTM.forEach((k) => {
      if (dato[k]) meta[k] = dato[k];
    });
    if (!Object.keys(meta).length) return false;
    // Se marca ANTES de mandar: logActivity encola y persiste por su cuenta (entrega al-menos-una-vez),
    // así que marcar después solo abriría la puerta a mandarlo dos veces si se recarga en medio.
    storage.setItem(CLAVE_ORIGEN, JSON.stringify({ ...dato, enviado: true }));
    log("registro_origen", { module: "authentication", meta });
    return true;
  } catch (_) {
    return false;
  }
}
